import type { AdminDashboardOutput } from "@/api/custom_types";
import { useApiError } from "@/hooks";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import {
	DashboardSection,
	SectionCard,
	StatTile,
} from "@/pages/dashboard/components";
import { useGetAdminDashboard } from "@/services";
import { formatNumber } from "@/utils/tools";
import { Button, ErrorBoundary, Icon, Typography } from "@st-marketplace/ui";
import { keepPreviousData } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { type FC, useEffect, useMemo, useState } from "react";
import { TicketCard, waitingFor } from "../tickets/TicketCard";
import { AdminOrdersCard } from "./AdminOrdersCard";
import { MissedAnalysisCard } from "./MissedAnalysisCard";

const HOUR = 60 * 60 * 1000;
const PERIODS = [
	{ key: "6h", hours: 6 },
	{ key: "24h", hours: 24 },
	{ key: "7d", hours: 7 * 24 },
	{ key: "30d", hours: 30 * 24 },
] as const;
type PeriodKey = (typeof PERIODS)[number]["key"];

/** The window slides once a minute, which is also what refetches the data. */
function useNow() {
	const [now, setNow] = useState(() => Date.now());
	useEffect(() => {
		const iv = setInterval(() => setNow(Date.now()), 60_000);
		return () => clearInterval(iv);
	}, []);
	return now;
}

const percentOrDash = (value: number | null | undefined) =>
	value === null || value === undefined ? "-" : formatNumber(value, "percent");

const PeriodPicker: FC<{
	value: PeriodKey;
	onChange: (value: PeriodKey) => void;
}> = ({ value, onChange }) => {
	const { t } = useTranslation();
	return (
		<fieldset
			className="dashboard-period-picker"
			aria-label={t("home.performance")}
		>
			{PERIODS.map(({ key, hours }) => (
				<label
					key={key}
					className={cn(
						"dashboard-period-option",
						value === key && "is-active",
					)}
				>
					<input
						type="radio"
						name="admin-dashboard-period"
						value={key}
						checked={value === key}
						onChange={() => onChange(key)}
						className="sr-only"
					/>
					{hours <= 24
						? t("home.lastHours", { time: hours })
						: t("home.lastDays", { time: hours / 24 })}
				</label>
			))}
		</fieldset>
	);
};

/** Shown above everything while any ticket is open. */
const OpenTicketsAlert: FC<{ tickets: AdminDashboardOutput["tickets"] }> = ({
	tickets,
}) => {
	const { t } = useTranslation();
	if (tickets.open === 0) return null;
	return (
		<div
			role="alert"
			className="flex flex-col gap-3 rounded-xl border border-danger-border bg-danger-100/10 p-4 sm:flex-row sm:items-center sm:justify-between"
		>
			<div className="flex items-center gap-3">
				<span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-danger-text text-white">
					<Icon name="alarm-warning-line" size="lg" />
				</span>
				<div>
					<Typography variant="body-md" as="p" className="font-semibold">
						{t("admin.dashboard.ticketsAlert", {
							open: tickets.open,
							urgent: tickets.urgent,
						})}
					</Typography>
					{tickets.oldest_open_at ? (
						<Typography variant="body-sm" as="p" className="text-danger-text">
							{t("admin.dashboard.oldestWaiting", {
								time: waitingFor(tickets.oldest_open_at),
							})}
						</Typography>
					) : null}
				</div>
			</div>
			<Link to="/st-admin/tickets">
				<Button type="button" color="danger" size="sm">
					{t("admin.dashboard.resolveTickets")}
				</Button>
			</Link>
		</div>
	);
};

/** Platform-wide: every user's orders, senders and tickets. */
const STAdminDashboard = () => {
	const { t } = useTranslation();
	const { getErrorInterface } = useApiError();
	const [period, setPeriod] = useState<PeriodKey>("24h");
	const now = useNow();

	const query = useMemo(() => {
		const hours = PERIODS.find((p) => p.key === period)?.hours ?? 24;
		return {
			start_datetime: new Date(now - hours * HOUR).toISOString(),
			end_datetime: new Date(now).toISOString(),
		};
	}, [period, now]);

	const { data, isLoading, error, refetch } = useGetAdminDashboard(
		{ query },
		{ placeholderData: keepPreviousData, refetchInterval: false },
	);
	const loading = isLoading && !data;

	return (
		<div className="flex flex-col gap-8">
			<Typography variant="title-md" as="h1">
				{t("admin.dashboard.title")}
			</Typography>

			{data ? <OpenTicketsAlert tickets={data.tickets} /> : null}

			<DashboardSection
				title={t("admin.dashboard.performance")}
				icon="line-chart-line"
				trailing={<PeriodPicker value={period} onChange={setPeriod} />}
			>
				<ErrorBoundary
					isLoading={false}
					error={error ? getErrorInterface(error) : undefined}
					onReset={refetch}
				>
					<div className="dashboard-stats-grid">
						<StatTile
							label={t("home.totalDelivered")}
							value={formatNumber(data?.total_delivered, "large_count")}
							hint={t("admin.dashboard.ofOrdered", {
								value: formatNumber(data?.total_ordered, "large_count"),
							})}
							icon="truck-line"
							loading={loading}
						/>
						<StatTile
							label={t("home.snipeSuccessRatio")}
							value={percentOrDash(data?.snipe_success_ratio)}
							icon="checkbox-circle-line"
							loading={loading}
						/>
						<StatTile
							label={t("home.globalTax")}
							value={formatNumber(data?.global_tax, "percent")}
							icon="percent-line"
							loading={loading}
						/>
						<StatTile
							label={t("admin.dashboard.quickSellErrors")}
							value={percentOrDash(data?.quick_sell.fail_percent)}
							hint={t("admin.dashboard.failedOf", {
								failed: data?.quick_sell.failed ?? 0,
								total: data?.quick_sell.total ?? 0,
							})}
							icon="flashlight-line"
							loading={loading}
						/>
						<StatTile
							label={t("admin.dashboard.snipeErrors")}
							value={percentOrDash(data?.snipe.fail_percent)}
							hint={t("admin.dashboard.failedOf", {
								failed: data?.snipe.failed ?? 0,
								total: data?.snipe.total ?? 0,
							})}
							icon="focus-3-line"
							loading={loading}
						/>
					</div>
				</ErrorBoundary>
			</DashboardSection>

			<AdminOrdersCard />

			<div className="grid gap-5 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
				<MissedAnalysisCard data={data?.missed_analysis} loading={loading} />
				<SectionCard
					title={t("admin.menuTickets")}
					viewAllTo="/st-admin/tickets"
					metric={
						data ? (
							<Typography
								variant="caption"
								as="p"
								className={
									data.tickets.open ? "text-danger-text" : "text-secondary-icon"
								}
							>
								{data.tickets.open
									? t("admin.dashboard.ticketsMetric", {
											open: data.tickets.open,
											urgent: data.tickets.urgent,
										})
									: t("admin.tickets.allClear")}
							</Typography>
						) : null
					}
				>
					<div className="flex flex-col gap-3 p-4">
						{data?.latest_open_tickets.map((ticket) => (
							<TicketCard key={ticket.id} ticket={ticket} compact />
						))}
					</div>
				</SectionCard>
			</div>

			<div className="h-24" />
		</div>
	);
};

export default STAdminDashboard;
